import { useMemo } from 'react';
import { createRouterSelector } from '@atlassian/react-resource-router/src/controllers/router-store';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	getIsSoftwareThemingEnabled,
	getIsSoftwareThemingM2Enabled,
} from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import {
	SOFTWARE_THEME_ROUTES,
	SOFTWARE_FULL_THEME_ROUTES,
	SOFTWARE_THEME_ROUTES_M2,
	SOFTWARE_FULL_THEME_ROUTES_M2,
} from './constants.tsx';

type SoftwareProjectTheming = {
	hasSoftwareTheming: boolean;
	isSoftwareFullTheming: boolean;
	isSoftwareProject: boolean;
};

const defaultValues: SoftwareProjectTheming = {
	hasSoftwareTheming: false,
	isSoftwareFullTheming: false,
	isSoftwareProject: false,
};

export const useCurrentRoute = createRouterSelector((state) => state.route.name);
export const useCurrentUrl = createRouterSelector((state) => state.match.url);

export const useSoftwareProjectThemingOld = (): SoftwareProjectTheming => {
	if (!getIsSoftwareThemingEnabled()) {
		return defaultValues;
	}

	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { data: projectData } = useProjectContext();
	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const currentRoute = useCurrentRoute();

	const isSoftwareProject = projectData?.projectType === SOFTWARE_PROJECT;

	if (!isSoftwareProject) {
		return defaultValues;
	}

	// temporarily disable theming for calendar
	// the logic matches with the one in src/packages/platform/project-theme/page-container/src/controllers/project-theme/index.tsx
	const isSoftwareCalendarRoute =
		currentRoute === 'software-calendar' || currentRoute === 'software-calendar-classic';

	if (isSoftwareCalendarRoute && !getIsSoftwareThemingM2Enabled()) {
		return {
			hasSoftwareTheming: false,
			isSoftwareFullTheming: false,
			isSoftwareProject,
		};
	}

	const hasSoftwareTheming = getIsSoftwareThemingM2Enabled()
		? isSoftwareProject && SOFTWARE_THEME_ROUTES_M2.has(currentRoute)
		: isSoftwareProject && SOFTWARE_THEME_ROUTES.has(currentRoute) && !isSoftwareCalendarRoute;
	const isSoftwareFullTheming = getIsSoftwareThemingM2Enabled()
		? hasSoftwareTheming && SOFTWARE_FULL_THEME_ROUTES_M2.has(currentRoute)
		: hasSoftwareTheming && SOFTWARE_FULL_THEME_ROUTES.has(currentRoute);

	return {
		hasSoftwareTheming,
		isSoftwareFullTheming,
		isSoftwareProject,
	};
};

export const getSoftwareThemedRoutes = () => {
	const allThemedSoftwareProjectRoutes = getIsSoftwareThemingM2Enabled()
		? SOFTWARE_THEME_ROUTES_M2
		: SOFTWARE_THEME_ROUTES;
	const fullThemedSoftwareProjectRoutes = getIsSoftwareThemingM2Enabled()
		? SOFTWARE_FULL_THEME_ROUTES_M2
		: SOFTWARE_FULL_THEME_ROUTES;

	// Enable theming for the shared routes no matter which milestone we are in
	allThemedSoftwareProjectRoutes.add('project-pages');
	allThemedSoftwareProjectRoutes.add('project-components');
	fullThemedSoftwareProjectRoutes.add('project-pages');

	return {
		allThemedSoftwareProjectRoutes,
		fullThemedSoftwareProjectRoutes,
	};
};

const useSoftwareProjectThemingSimplified = (): SoftwareProjectTheming => {
	const currentRoute = useCurrentRoute();
	const currentUrl = useCurrentUrl();
	const { allThemedSoftwareProjectRoutes, fullThemedSoftwareProjectRoutes } =
		getSoftwareThemedRoutes();
	const isSoftwareProject =
		allThemedSoftwareProjectRoutes.has(currentRoute) &&
		(currentUrl.includes('/jira/software/projects/') ||
			currentUrl.includes('/jira/software/c/projects/') ||
			currentRoute === 'issue');
	const isSoftwareCalendarRoute =
		currentRoute === 'software-calendar' || currentRoute === 'software-calendar-classic';

	return useMemo(() => {
		if (!isSoftwareProject || !getIsSoftwareThemingEnabled()) {
			return defaultValues;
		}

		// temporarily disable theming for calendar
		// the logic matches with the one in src/packages/platform/project-theme/page-container/src/controllers/project-theme/index.tsx
		if (isSoftwareCalendarRoute && !getIsSoftwareThemingM2Enabled()) {
			return {
				hasSoftwareTheming: false,
				isSoftwareFullTheming: false,
				isSoftwareProject,
			};
		}

		return {
			hasSoftwareTheming: allThemedSoftwareProjectRoutes.has(currentRoute),
			isSoftwareFullTheming: fullThemedSoftwareProjectRoutes.has(currentRoute),
			isSoftwareProject,
		};
	}, [
		isSoftwareProject,
		isSoftwareCalendarRoute,
		allThemedSoftwareProjectRoutes,
		currentRoute,
		fullThemedSoftwareProjectRoutes,
	]);
};

export const useSoftwareProjectTheming = functionWithCondition(
	() => expVal('performance_improvement_in_use_project_theme', 'isEnabled', false),
	useSoftwareProjectThemingSimplified,
	useSoftwareProjectThemingOld,
);
